/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, makeStyles } from '@material-ui/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import GetAppIcon from '@material-ui/icons/GetApp';
import i18n from 'i18n';
import { getAllClientsRequest } from 'modules/clients/actions';
import {
  ListClientsAppsFields,
  ListClientsAppsProps,
} from 'modules/environments/interfaces';

import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import { getClientsApplicationsAutoskyRequest } from 'modules/environments/actions';
import ScreenStatesApplications from 'containers/Clients/ApplicationsClients/ScreenStatesApplications';
import Button from 'components/Button';
import palette from 'theme/palette';
import { modalClose } from 'modules/modal/actions';
import { messagesRequest } from 'modules/messages/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 100,
  search: '',
};

interface Props {
  appName: string;
  environmentName: string | undefined;
  isLoadingClientsApps: boolean;
  clientsApplicationsList: ListClientsAppsProps;
  getClientsApplicationsAutoskyRequest: (props) => void;
  modalClose: () => void;
}

const ListApplicationClients = ({
  appName,
  environmentName,
  isLoadingClientsApps,
  clientsApplicationsList,
  getClientsApplicationsAutoskyRequest,
  modalClose,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const { id } = useParams<{ id: string }>();
  const getClientsApplicationsAutosky = () => {
    getClientsApplicationsAutoskyRequest({
      id,
      query,
      name: appName,
    });
  };

  React.useEffect(() => {
    getClientsApplicationsAutosky();
  }, [getClientsApplicationsAutoskyRequest, id, query, appName]);

  const mapState = (state: 'active' | 'inactive'): string => {
    const options = {
      active: i18n.t('LOGS_PAGE.ACTIVE_KEY'),
      inactive: i18n.t('LOGS_PAGE.INACTIVE_KEY'),
    };

    return options[state] || state;
  };

  const getClientsListPDF: Array<[
    string,
    string
  ]> = clientsApplicationsList?.results.map(item => [
    item?.client?.name,
    mapState(item?.state),
  ]);

  // eslint-disable-next-line new-cap
  const doc = new jsPDF();

  const generatePDFButton = () => {
    doc.setFontSize(16);
    doc.text(
      [
        '',
        `${i18n.t('ENVIRONMENT_NAME_KEY')}: ${environmentName}`,
        `${i18n.t('APPLICATIONS_NAME_KEY')}: ${appName}`,
        `${i18n.t('PDF_GENERATED_AT_KEY')}: ${new Date().toLocaleString(
          'pt-BR',
          {
            hour12: false,
          }
        )}`,
        `${i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}:`,
        '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    autoTable(doc, {
      head: [[`${i18n.t('CLIENT_NAME_KEY')}`, 'Status']],
      margin: { top: 50 },
      styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 11 },
      headStyles: {
        fillColor: palette.deepBlue,
        textColor: palette.offWhite,
        fontSize: 11,
      },
      body: getClientsListPDF,
    });
    doc.save(`${appName}.pdf`);
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingClientsApps}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              <TableCell className={classes.tableRow}>
                {i18n.t('CLIENT_NAME_KEY')}
              </TableCell>
              <TableCell centerTitle className={classes.state}>
                {i18n.t('STATUS_KEY')}
              </TableCell>
            </>
          }
          renderItems={(item: ListClientsAppsFields, index) => (
            <TableRow key={index}>
              <TableCell>{item?.client.name}</TableCell>
              <TableCell textAlignCenter centerTitle>
                <ScreenStatesApplications type={item?.state} />
              </TableCell>
            </TableRow>
          )}
          overrideRowsCount={100}
          colCount={2}
          isLoading={isLoadingClientsApps}
          data={clientsApplicationsList}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="generate-pdf-button"
          submitButton
          startIcon={<GetAppIcon />}
          onClick={() => {
            generatePDFButton();
            modalClose();
            messagesRequest({
              type: 'success',
              message: i18n.t('PDF_GENERATED_SUCCESSFULLY_KEY'),
            });
          }}
        >
          {i18n.t('GENERATE_PDF')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  environments: {
    isLoadingClientsApps,
    clientsApplicationsList,
    environmentSelected,
  },
}: ApplicationState) => ({
  isLoadingClientsApps,
  clientsApplicationsList,
  environmentSelected,
});

export default connect(mapStateToProps, {
  getClientsApplicationsAutoskyRequest,
  getAllClientsRequest,
  modalClose,
})(ListApplicationClients);
